/**
 * Hook to fetch optimization configuration from the backend API.
 * This allows feature flags like coldstartEnabled to be controlled via config.json
 * without requiring separate frontend environment variables.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';

export interface OptimizationConfig {
  coldstartEnabled?: boolean;
  coldstartFilterStartdateToMostRecentApplication?: boolean;
  minimumColdStartLookback?: number;
  budgetAllocationLookbackDays?: number;
}

// Default values used as placeholder during loading
// Fails closed (coldstartEnabled: false) to prevent showing coldstart UI
// when the feature flag state is uncertain
const DEFAULT_CONFIG: OptimizationConfig = {
  coldstartEnabled: false, // Fail closed - hide coldstart UI if config can't be loaded
  coldstartFilterStartdateToMostRecentApplication: false,
  minimumColdStartLookback: 7,
  budgetAllocationLookbackDays: 28,
};

/**
 * Fetch optimization configuration from the backend.
 * Caches the result for the session to minimize API calls.
 *
 * Callers should check isError to distinguish between "config loaded with
 * coldstart disabled" vs "config couldn't be loaded". When isError is true,
 * data may be undefined (placeholderData is not retained after error).
 * Use useColdstartEnabled() for a simple fail-closed boolean check.
 */
export function useOptimizationConfig() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['optimization-config'],
    queryFn: async (): Promise<OptimizationConfig> => {
      const response = await apiClient.get('/config/optimization');
      debug.log('useOptimizationConfig: Loaded config from API', response.data);
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: Infinity, // Cache forever (until logout/refresh)
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 2, // Retry twice before failing
    retryDelay: 1000, // Wait 1s between retries
    // Placeholder while loading (before any fetch attempt)
    placeholderData: DEFAULT_CONFIG,
  });
}

/**
 * Check if coldstart feature is enabled.
 * Returns false (fail closed) if:
 * - Config hasn't loaded yet (isFetching)
 * - Config fetch failed (isError)
 * - Config loaded but coldstartEnabled is false or undefined
 */
export function useColdstartEnabled(): boolean {
  const { data, isError, isFetching } = useOptimizationConfig();

  // Fail closed: return false if loading, errored, or explicitly disabled
  if (isFetching || isError) {
    return false;
  }

  return data?.coldstartEnabled ?? false;
}
